// Re:Focus — Storage Module
// localStorage persistence + cloud save/sync with dependency injection

let _getAuthHeader = () => ({});
let _getVersion = () => 0;
let _setVersion = (v) => {};
let _getUserKey = () => 'default';
let _softLogout = () => {};
let _flashSaved = () => {};
let _flashToast = () => {};
let _renderFromState = () => {};

export function initStorage({ getAuthHeader, getVersion, setVersion, getUserKey, softLogout, flashSaved, flashToast, renderFromState }) {
  _getAuthHeader = getAuthHeader;
  _getVersion = getVersion;
  _setVersion = setVersion;
  _getUserKey = getUserKey;
  _softLogout = softLogout;
  _flashSaved = flashSaved;
  _flashToast = flashToast;
  _renderFromState = renderFromState;
}

// Legacy key for migration
export const OVERRIDES_KEY_LEGACY = 'refocus_overrides';

export function getOverridesKey() {
  const user = _getUserKey();
  return `refocus_overrides_${user}`;
}

export function loadOverrides() {
  try {
    const key = getOverridesKey();
    const data = localStorage.getItem(key);
    if (data) return JSON.parse(data) || {};
    // Migrate legacy single-user key if present
    const legacy = localStorage.getItem(OVERRIDES_KEY_LEGACY);
    if (legacy) {
      const parsed = JSON.parse(legacy) || {};
      localStorage.setItem(key, legacy);
      localStorage.removeItem(OVERRIDES_KEY_LEGACY);
      return parsed;
    }
    return {};
  } catch { return {}; }
}

let _cloudSaveTimer = null;

export function saveOverrides(ov) {
  ov._savedAt = Date.now(); // stamp every save so cloudSync can compare
  // Instant local save (no UI lag)
  localStorage.setItem(getOverridesKey(), JSON.stringify(ov));
  // Debounced cloud save — 1s after last change
  clearTimeout(_cloudSaveTimer);
  _cloudSaveTimer = setTimeout(() => cloudSave(ov), 1000);
}

export async function cloudSave(ov) {
  try {
    const payload = { ...ov, _version: _getVersion() };
    const resp = await fetch('/api/overrides', {
      method: 'PUT',
      headers: { ..._getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (resp.status === 401) { _softLogout(); return; }
    if (resp.status === 409) {
      _flashToast('Refreshing your board — another tab had changes');
      await cloudSync();
      _renderFromState();
      return;
    }
    if (resp.status === 413) {
      _flashToast('Board is too large to save. Archive completed projects to reduce size.');
      return;
    }
    if (!resp.ok) {
      console.warn('[Re:Focus] Cloud save failed with status:', resp.status);
      const banner = document.getElementById('offline-banner');
      if (banner) banner.classList.remove('hidden');
      // Retry with exponential backoff — max 3 attempts (5s, 15s, 45s), then give up
      const attempt = (ov._saveAttempt || 0) + 1;
      if (attempt <= 3) {
        const delay = 5000 * Math.pow(3, attempt - 1);
        setTimeout(() => { cloudSave({ ...loadOverrides(), _saveAttempt: attempt }); }, delay);
      } else {
        console.warn('[Re:Focus] Cloud save failed after 3 retries — giving up until next user action');
        delete ov._saveAttempt;
        _flashToast('Changes saved locally — will sync when reconnected');
      }
      return;
    }
    const result = await resp.json().catch(() => ({}));
    if (result._version) _setVersion(result._version);
    if (resp.headers.get('X-Board-Size-Warning')) {
      _flashToast('Your board is getting large. Archive completed projects to keep things fast.');
    }
    // Clear retry counter on success — write directly to localStorage to avoid
    // triggering another debounced cloudSave() via saveOverrides()
    const fresh = loadOverrides(); delete fresh._saveAttempt;
    fresh._savedAt = Date.now();
    localStorage.setItem(getOverridesKey(), JSON.stringify(fresh));
    document.getElementById('offline-banner')?.classList.add('hidden');
    _flashSaved();
  } catch (e) {
    console.warn('[Re:Focus] Cloud save failed (offline?):', e);
    const banner = document.getElementById('offline-banner');
    if (banner) banner.classList.remove('hidden');
    _flashToast('Changes saved locally — will sync when reconnected');
    // Global online listener (app.js init) handles reconnect sync — no duplicate needed here
  }
}

export async function cloudSync() {
  try {
    const resp = await fetch('/api/overrides', { headers: _getAuthHeader() });
    if (resp.status === 401) { _softLogout(); return; }
    if (!resp.ok) return;
    const cloud = await resp.json();
    if (cloud._version !== undefined) _setVersion(cloud._version);
    const cloudKeys = Object.keys(cloud).length;
    if (cloudKeys === 0) return; // nothing in cloud yet
    // Cloud wins only on a genuinely fresh browser (localStorage completely empty)
    // Never overwrite local state that already has data — local is always more recent
    const local = loadOverrides();
    const cloudTime = cloud._savedAt || 0;
    const localTime = local._savedAt || 0;
    // Cloud wins if: local is empty OR cloud is strictly newer (ties go to local — local is always fresher)
    if (Object.keys(local).length === 0 || cloudTime > localTime) {
      // Guard: don't clobber unsaved input content
      const ae = document.activeElement;
      const isTyping = ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA');
      const hasUnsaved = !isTyping && Array.from(
        document.querySelectorAll('.add-task-input, .progress-note-input')
      ).some(el => el.value && el.value.trim().length > 0);
      if (isTyping || hasUnsaved) return; // skip sync until user is done typing
      localStorage.setItem(getOverridesKey(), JSON.stringify(cloud));
      _renderFromState();
    }
  } catch (e) {
    console.warn('[Re:Focus] Cloud sync failed (offline?):', e);
  }
}
